import React, { useEffect, useState } from "react";
import { FlightFilters, FlightSearch, Weather } from "../../../../../../Components";
import { Button, Loader, SidePaneContent, Toast } from "../../../../../../Components";
import { useSearchParams } from "react-router-dom";
import { apis, get } from "../../../../../../Utils/axios";
import FlightResult from "./Result/domestic";
import Summary from "./Result/Components/summary";
import moment from "moment";
import { useUserPreference } from "../../../../../../Hooks/useUserPreference";
import useMobileCheck from "../../../../../../Hooks/useMobileCheck";
import { FiChevronDown, FiChevronUp, FiEdit2, FiX } from "react-icons/fi";

function RoundSRP() {
	const [searchParams] = useSearchParams();
	const { showSidePane } = useUserPreference();
	const { isMobile } = useMobileCheck();
	const from = searchParams.get("from");
	const to = searchParams.get("to");
	const fromCode = searchParams.get("fromCode");
	const fromCity = searchParams.get("fromCity");
	const toCode = searchParams.get("toCode");
	const toCity = searchParams.get("toCity");
	const date = parseInt(searchParams.get("date"));
	const returnDate = parseInt(searchParams.get("returnDate"));
	const adult = parseInt(searchParams.get("adult")) || 1;
	const child = parseInt(searchParams.get("child")) || 0;
	const infant = parseInt(searchParams.get("infant") || 0);
	const cabinClass = searchParams.get("cabinClass") || "1";
	const requestId = searchParams.get("requestId") || "";
	const approvalId = searchParams.get("approvalId") || "";
	const optimize = searchParams.get("optimize") || false;
	const [Loading, setLoading] = useState(true);
	const [SelectedFlight, setSelectedFlight] = useState();
	const [ShowFiltersPane, setShowFiltersPane] = useState(false);
	const [ShowSearchForm, setShowSearchForm] = useState(false);
	const [Result, setResult] = useState({
		flights: [],
		journeyKey: "",
		isInternational: false,
	});

	const [Filters, setFilters] = useState({
		maxPrice: 0,
		stops: [],
		airlines: [],
		time: [],
		inPolicy: false,
		fullyRefundable: false,
	});

	useEffect(() => {
		searchFlights({ fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId, optimize });
	}, [fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId, optimize]);

	useEffect(() => {
		setSelectedFlight(undefined);
		// Keep side pane closed by default across breakpoints.
		// User explicitly opens filters via the Filter action.
		setShowFiltersPane(false);
		showSidePane(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isMobile]);

	const searchFlights = ({ fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId, optimize }) => {
		setLoading(true);
		setSelectedFlight();
		if (isMobile) {
			setShowFiltersPane(false);
			showSidePane(false);
		}
		get(apis.getRoundFlights, { from: fromCode?.toUpperCase(), to: toCode?.toUpperCase(), date: moment(date).format("DD-MM-YYYY"), returnDate: moment(returnDate).format("DD-MM-YYYY"), adult, child, infant, cabinClass, requestId, approvalId, optimize }, (r, e) => {
			if (r) {
				setResult(r);
				setLoading(false);
			} else if (e) {
				Toast.handleError(e);
				setLoading(false);
			}
		});
	};

	const handleSelectFlight = (flight) => {
		setSelectedFlight(flight);
		if (flight) {
			setShowFiltersPane(false);
			showSidePane(true);
		}
	};

	const openFilters = () => {
		setSelectedFlight(undefined);
		setShowFiltersPane(true);
		showSidePane(true);
	};

	const closeSidePane = () => {
		setShowFiltersPane(false);
		setSelectedFlight(undefined);
		showSidePane(false);
	};

	const sidePaneNode = SelectedFlight ? (
		<div className="flex flex-col flex-1 min-h-0 h-full">
			<Summary
				{...{
					approval: Result.approval,
					passengers: { adult, child, infant },
					selectedFlight: SelectedFlight,
					approvalId,
					requestId,
					fromCode,
					toCode,
					date,
					returnDate,
					adult,
					child,
					infant,
					cabinClass,
					onClose: closeSidePane,
				}}
			/>
		</div>
	) : ShowFiltersPane ? (
		<div className="flex flex-col h-full min-h-0 overflow-hidden">
			<div className="flex-shrink-0 p-4 pb-3 border-b border-surface bg-canvas">
				<div className="flex items-center justify-between gap-3">
					<h5 className="text-sm font-bold">Filters</h5>
					<Button variant="outlined" className="btn-xs shrink-0" onClick={closeSidePane}>
						<FiX className="mr-1" /> Close
					</Button>
				</div>
			</div>
			<div className="flex-1 min-h-0 p-4 pt-3">
				<FlightFilters currency={Result.currency} flights={Result.flights} filters={Filters} updateFilters={setFilters} />
			</div>
		</div>
	) : null;

	return (
		<>
			<Loader loading={Loading} />
			{sidePaneNode ? <SidePaneContent>{sidePaneNode}</SidePaneContent> : null}

			<div className="relative p-3 md:p-4 min-w-0">
				{isMobile ? (
					<div className="mb-3 overflow-hidden border rounded-lg bg-canvas border-surface">
						<div className="flex items-start justify-between gap-2 p-3">
							<div className="min-w-0">
								<div className="text-[11px] text-secondary uppercase tracking-wide">Travel context</div>
								<div className="mt-1 text-sm font-semibold truncate">
									{fromCode?.toUpperCase()} to {toCode?.toUpperCase()}
								</div>
								<div className="mt-1 text-xs text-secondary truncate">
									{moment(date).format("DD MMM")} - {moment(returnDate).format("DD MMM YYYY")} | {adult} Adult
									{child > 0 ? `, ${child} Child` : ""}
									{infant > 0 ? `, ${infant} Infant` : ""} | Cabin {cabinClass}
								</div>
							</div>
							<Button variant="outlined" className="btn-xs shrink-0" onClick={() => setShowSearchForm((prev) => !prev)}>
								<FiEdit2 className="mr-1" />
								Modify
								{ShowSearchForm ? <FiChevronUp className="ml-1" /> : <FiChevronDown className="ml-1" />}
							</Button>
						</div>
						{ShowSearchForm && (
							<div className="p-2 border-t border-surface">
								<FlightSearch
									mode={{ label: "Search Round Flights", value: "round" }}
									from={{ airportName: from, airportCode: fromCode, cityName: fromCity }}
									to={{ airportName: to, airportCode: toCode, cityName: toCity }}
									date={date}
									range={{ startDate: date, endDate: returnDate }}
									passengers={{ adult, child, infant }}
									cabinClass={{ value: cabinClass }}
									optimizee={optimize}
									onSearch={() => setShowSearchForm(false)}
								/>
							</div>
						)}
					</div>
				) : (
					<FlightSearch
						mode={{ label: "Search Round Flights", value: "round" }}
						from={{ airportName: from, airportCode: fromCode, cityName: fromCity }}
						to={{ airportName: to, airportCode: toCode, cityName: toCity }}
						date={date}
						range={{ startDate: date, endDate: returnDate }}
						passengers={{ adult, child, infant }}
						cabinClass={{ value: cabinClass }}
						optimizee={optimize}
						onSearch={() => setShowSearchForm(false)}
					/>
				)}
				{Loading ? (
					<div className="w-full mt-20 flex-center">
						<div>
							<span className="text-sm text-secondary">Hang tight, getting the best fares for you ...</span>
							{/* <Weather place={toCity} className="mt-6" /> */}
						</div>
					</div>
				) : (
					<FlightResult approval={Result.approval} filters={Filters} flights={Result.flights} onSelect={handleSelectFlight} SelectedFlight={SelectedFlight} onOpenFilters={openFilters} isMobile={isMobile} {...{ fromCode, toCode, date, returnDate, adult, child, infant, cabinClass, requestId, approvalId }} />
				)}
			</div>
			{(Loading || !(Result.onwardFlights?.length || Result.returnFlights?.length)) && (
				<>
					<video className="absolute bottom-0 left-0 object-cover w-full h-full -z-10" autoPlay muted loop>
						<source src="https://cdn.travelwings.com/assets/corp/videos/clouds.mp4" type="video/mp4" />
					</video>
					<div className="absolute top-0 bottom-0 left-0 right-0 w-full h-full -z-10" style={{ background: "linear-gradient(to bottom, rgba(255,255,255,1) 70%,rgba(255,255,255,0) 100%)" }} />
					<div className="absolute top-0 bottom-0 left-0 right-0 w-full h-full -z-10" style={{ background: "linear-gradient(to right, rgba(255,255,255,1) 0%,rgba(255,255,255,0) 20%)" }} />
					<div className="absolute top-0 bottom-0 left-0 right-0 w-full h-full -z-10" style={{ background: "linear-gradient(to left, rgba(255,255,255,1) 0%,rgba(255,255,255,0) 20%)" }} />
				</>
			)}
		</>
	);
}

export default RoundSRP;
